import { Component, Prop, h } from '@stencil/core';
import { getConstant } from '../../../../utils/constants-helpers';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

/**
 * Breadcrumb navigation component for SignNow brand
 *
 * @product signnow
 * @source breadcrumb
 * @version 1.0.0
 * @category breadcrumbs
 * @contentConfig Breadcrumb component for displaying navigation path
 * @constant BREADCRUMB_ARIA_LABEL - "Breadcrumb" - Accessible label for breadcrumb navigation
 * @constant BREADCRUMB_SEPARATOR - "arrows--chevron-right" - Icon name for separator
 */
@Component({
  tag: 'sn-breadcrumbs-type-1',
  shadow: false,
})
export class SNBreadcrumbsType1 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop({ mutable: true }) constants?: Record<string, string> | string;

  /**
   * @dynamic
   * @type {array}
   * @.type {object}
   * @.property {string} title - The title text for breadcrumb item
   * @.property {string} link - The URL link (optional, if not provided item will be non-clickable)
   * @.required title
   * @minItems 1
   */
  @Prop({ mutable: true }) items!: any[] | string;

  /**
   * Icon name for separator between items
   */
  @Prop() separator: string = 'arrows--chevron-right';

  /**
   * Theme variant for the breadcrumb component
   */
  @Prop() theme: 'light' | 'dark' = 'light';

  /**
   * Add schema.org microdata markup
   */
  @Prop() withSchemaOrgEntities: boolean = true;

  private parsedItems: any[] = [];

  componentWillLoad() {
    this.constants = parseJsonProp(this.constants);
    this.items = parseJsonProp(this.items, []);

    // Transform server format {title, link} to component format {label, href}
    this.parsedItems = Array.isArray(this.items)
      ? this.items.map(item => ({
          label: item.label || item.title || '',
          href: item.href || item.link || '',
        }))
      : [];
  }

  render() {
    // Pass array directly - Stencil handles this correctly during SSR
    // The child component will receive array in componentWillLoad
    return (
      <breadcrumbs-type-1
        items={this.parsedItems}
        separator="arrows--chevron-right"
        ariaLabel={getConstant(this.constants, 'BREADCRUMB_ARIA_LABEL', 'Breadcrumb')}
        theme={this.theme}
        withSchemaOrgEntities={this.withSchemaOrgEntities}
      />
    );
  }
}
